/**
 * L'écran d'administration des regroupements G1-G10.
 *
 * Les dix familles de travaux du PRD §4.3, telles que le métier les tient : on
 * renomme, on recolore, on désactive celles qu'on n'emploie pas, et surtout **on
 * enrichit le dictionnaire de mots-clés**.
 *
 * Ce dernier point est le vrai sujet de cet écran. Mesuré sur 19 déboursés réels,
 * les mots-clés reconnaissent 62 % des chapitres — et ce qui leur résiste est du
 * vocabulaire de travaux publics : « AEP », « LIBAGES », « SEMELLES FILANTES »,
 * « MISE AUX NORMES ». Aucune règle ne le déduira, mais un homme du métier le
 * classe en deux secondes, et une fois classé c'est acquis pour tous les chantiers
 * suivants. **Cet écran est donc l'endroit où le produit apprend le métier.**
 *
 * Trois partis pris, repris du référentiel des ressources génériques :
 *
 *  - **désactiver n'efface pas.** Un groupe retiré sort des listes de choix et des
 *    propositions, mais reste lisible sur les chantiers qui l'emploient ;
 *  - **les groupes inactifs restent affichés**, grisés. Un référentiel qui cache ce
 *    qu'il contient ne s'administre pas ;
 *  - **le code se renomme, et le référentiel s'agrandit** (migration 022). Aucun des
 *    deux gestes ne touche aux contre-études chargées : les postes, la mémoire des
 *    affectations et les mots-clés désignent l'identifiant d'un groupe, jamais son
 *    code. Un groupe créé ici n'attire d'ailleurs rien tant qu'on ne lui a pas donné
 *    de mots-clés.
 */
import { useEffect, useState } from "react";
import type { JSX } from "react";
import type { GroupeAdmin } from "@planif/moteur";
import {
  ajouterMotCle,
  chargerGroupes,
  creerGroupe,
  deplacerGroupe,
  modifierGroupe,
  retirerMotCle,
} from "./api";

/** Ce que `enAttente` porte pendant une création, qui n'a pas encore d'identifiant. */
const NOUVEAU = "nouveau";

type Etat =
  | { phase: "chargement" }
  | { phase: "chargee"; groupes: readonly GroupeAdmin[] }
  | { phase: "erreur"; raison: string };

/** Un champ qui n'envoie sa valeur qu'une fois la saisie terminée. */
function Champ({
  valeur,
  enAttente,
  onValider,
}: {
  valeur: string;
  enAttente: boolean;
  onValider: (valeur: string) => void;
}): JSX.Element {
  const [brouillon, setBrouillon] = useState<string | null>(null);

  return (
    <input
      className={`saisie saisie--texte${enAttente ? " saisie--attente" : ""}`}
      value={brouillon ?? valeur}
      disabled={enAttente}
      onChange={(e) => {
        setBrouillon(e.target.value);
      }}
      onFocus={(e) => {
        e.target.select();
      }}
      onBlur={() => {
        const saisi = brouillon;
        setBrouillon(null);
        if (saisi !== null && saisi.trim() !== valeur) onValider(saisi.trim());
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setBrouillon(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** Le dictionnaire d'un groupe : ses mots-clés, avec l'ajout et le retrait. */
function MotsCles({
  groupe,
  onAjouter,
  onRetirer,
}: {
  groupe: GroupeAdmin;
  onAjouter: (mot: string) => void;
  onRetirer: (mot: string) => void;
}): JSX.Element {
  const [nouveau, setNouveau] = useState("");

  return (
    <div className="mots-cles">
      <ul className="mots-cles__liste">
        {groupe.mots.map((mot) => (
          <li key={mot}>
            <code>{mot}</code>
            <button
              type="button"
              className="mots-cles__retirer"
              title={`Retirer « ${mot} »`}
              onClick={() => {
                onRetirer(mot);
              }}
            >
              ×
            </button>
          </li>
        ))}
        {groupe.mots.length === 0 && <li className="vide">aucun mot-clé</li>}
      </ul>

      <form
        className="mots-cles__ajout"
        onSubmit={(e) => {
          e.preventDefault();
          if (nouveau.trim() === "") return;
          onAjouter(nouveau.trim());
          setNouveau("");
        }}
      >
        <input
          value={nouveau}
          placeholder="AEP, LIBAGES, SEMELLES FILANTES…"
          aria-label={`Nouveau mot-clé pour ${groupe.libelle}`}
          onChange={(e) => {
            setNouveau(e.target.value);
          }}
        />
        <button type="submit" disabled={nouveau.trim() === ""}>
          Ajouter
        </button>
      </form>
    </div>
  );
}

/**
 * Le formulaire de création.
 *
 * Une couleur est demandée d'emblée, et pré-remplie : sans elle le groupe sort en
 * gris sur le planning, indiscernable du voisin — et personne ne pense à revenir la
 * poser. Le rang, lui, n'est pas demandé ; le serveur place le nouveau venu en
 * dernier pour qu'il ne prenne aucun poste aux groupes déjà en place.
 */
function Ajout({
  enAttente,
  onCreer,
}: {
  enAttente: boolean;
  onCreer: (champs: { code: string; libelle: string; couleur: string }) => void;
}): JSX.Element {
  const [code, setCode] = useState("");
  const [libelle, setLibelle] = useState("");
  const [couleur, setCouleur] = useState("#808080");

  const complet = code.trim() !== "" && libelle.trim() !== "";

  return (
    <form
      className="groupes__ajout"
      onSubmit={(e) => {
        e.preventDefault();
        if (!complet) return;
        onCreer({ code: code.trim(), libelle: libelle.trim(), couleur });
        setCode("");
        setLibelle("");
        setCouleur("#808080");
      }}
    >
      <input
        className="saisie saisie--texte"
        value={code}
        placeholder="G11"
        aria-label="Code du nouveau regroupement"
        disabled={enAttente}
        onChange={(e) => {
          setCode(e.target.value);
        }}
      />
      <input
        type="color"
        className="groupes__couleur"
        value={couleur}
        aria-label="Couleur du nouveau regroupement"
        disabled={enAttente}
        onChange={(e) => {
          setCouleur(e.target.value);
        }}
      />
      <input
        className="saisie saisie--texte"
        value={libelle}
        placeholder="Nom du regroupement"
        aria-label="Nom du nouveau regroupement"
        disabled={enAttente}
        onChange={(e) => {
          setLibelle(e.target.value);
        }}
      />
      <button type="submit" disabled={!complet || enAttente}>
        Ajouter le regroupement
      </button>
    </form>
  );
}

export function Groupes(): JSX.Element {
  const [etat, setEtat] = useState<Etat>({ phase: "chargement" });
  const [enAttente, setEnAttente] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const recharger = (): void => {
    chargerGroupes()
      .then(({ groupes }) => {
        setEtat({ phase: "chargee", groupes });
      })
      .catch((e: unknown) => {
        setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
      });
  };

  useEffect(recharger, []);

  const agir = (promesse: Promise<unknown>, id: string | null = null): void => {
    setEnAttente(id);
    setErreur(null);
    promesse
      .then(recharger)
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(null);
      });
  };

  const groupes = etat.phase === "chargee" ? etat.groupes : [];
  const actifs = groupes.filter((g) => g.actif).length;
  const mots = groupes.reduce((n, g) => n + g.mots.length, 0);

  return (
    <>
      <p className="etat">
        Les dix familles de travaux du chantier. Chaque tâche en porte une, et c'est par elles que
        le terrain déclarera son travail — le chef dit « je suis en terrassement », jamais
        « tâche 1.4.3 ». La couleur est celle des barres du planning.
      </p>

      <p className="etat">
        Les <strong>mots-clés</strong> sont ce qui permet à l'application de proposer un
        regroupement toute seule : elle cherche ces fragments dans le libellé des postes. Mesuré
        sur 19 déboursés réels, ils en reconnaissent 62 % — le reste est du vocabulaire de travaux
        publics à ajouter ici, une fois pour tous les chantiers à venir.
      </p>

      <p className="etat">
        Le <strong>rang</strong> tranche les libellés qui parlent de deux choses à la fois :
        « TERRASSEMENT ET NIVELLEMENT POUR ASSAINISSEMENT » contient les mots-clés de deux
        groupes, et c'est le plus haut de cette liste qui l'emporte. Les groupes les plus
        spécifiques ont donc intérêt à passer avant les plus généraux — un poste qui parle de
        signalisation est de la signalisation, même s'il mentionne la voirie où elle se pose.
      </p>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {etat.phase === "chargement" && <p>Chargement des regroupements…</p>}
      {etat.phase === "erreur" && <p className="alerte">{etat.raison}</p>}

      {etat.phase === "chargee" && groupes.length === 0 && (
        <p className="alerte">
          Aucun regroupement en base. Charger ceux du métier d'un coup :{" "}
          <code>pnpm importer-groupes</code> — ou les créer un par un ci-dessous.
        </p>
      )}

      {etat.phase === "chargee" && groupes.length > 0 && (
        <>
          <p className="etat">
            {actifs} regroupement(s) actif(s) sur {groupes.length}, {mots} mots-clés au total.
          </p>

          <table className="groupes">
            <thead>
              <tr>
                <th title="Priorité : quand un libellé contient les mots-clés de plusieurs groupes, c'est le plus haut de cette liste qui gagne.">
                  Rang
                </th>
                <th>Code</th>
                <th>Couleur</th>
                <th>Nom du groupe</th>
                <th>Mots-clés qui le proposent</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {groupes.map((g, rang) => (
                <tr key={g.id} className={g.actif ? undefined : "retiree"}>
                  <td className="groupes__rang">
                    {/* Deux boutons plutôt qu'un champ : le rang vaut pour une notion
                        relative — « la signalisation avant la voirie » — et personne ne
                        connaît son numéro. Le premier ne monte pas, le dernier ne
                        descend pas : un bouton qui ne fait rien vaut mieux désactivé. */}
                    <button
                      type="button"
                      className="lien"
                      title="Monter d'un cran : ce groupe proposera avant celui du dessus"
                      disabled={rang === 0 || enAttente === g.id}
                      onClick={() => {
                        agir(deplacerGroupe(g.id, "monter"), g.id);
                      }}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="lien"
                      title="Descendre d'un cran"
                      disabled={rang === groupes.length - 1 || enAttente === g.id}
                      onClick={() => {
                        agir(deplacerGroupe(g.id, "descendre"), g.id);
                      }}
                    >
                      ↓
                    </button>
                  </td>
                  <td className="code">
                    {/* Renommable depuis la 022. Ce qui l'interdisait n'était pas
                        les données — rien ne s'accroche au code — mais l'outil
                        d'amorçage, qui se repère désormais sur `code_amorce`. */}
                    <Champ
                      valeur={g.code}
                      enAttente={enAttente === g.id}
                      onValider={(code) => {
                        agir(modifierGroupe(g.id, { code }), g.id);
                      }}
                    />
                  </td>
                  <td>
                    {/* Le nuancier natif : la couleur se choisit à l'œil, et c'est
                        la seule façon de vérifier qu'elle se distingue des neuf autres. */}
                    <input
                      type="color"
                      className="groupes__couleur"
                      value={g.couleur ?? "#808080"}
                      disabled={enAttente === g.id}
                      aria-label={`Couleur de ${g.libelle}`}
                      onChange={(e) => {
                        agir(modifierGroupe(g.id, { couleur: e.target.value }), g.id);
                      }}
                    />
                  </td>
                  <td>
                    <Champ
                      valeur={g.libelle}
                      enAttente={enAttente === g.id}
                      onValider={(libelle) => {
                        agir(modifierGroupe(g.id, { libelle }), g.id);
                      }}
                    />
                  </td>
                  <td>
                    <MotsCles
                      groupe={g}
                      onAjouter={(mot) => {
                        agir(ajouterMotCle(g.id, mot), g.id);
                      }}
                      onRetirer={(mot) => {
                        agir(retirerMotCle(mot), g.id);
                      }}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="lien"
                      disabled={enAttente === g.id}
                      onClick={() => {
                        agir(modifierGroupe(g.id, { actif: !g.actif }), g.id);
                      }}
                    >
                      {g.actif ? "Retirer" : "Remettre"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="etat">
            Un groupe retiré sort des listes de choix et ne propose plus rien, mais reste affiché
            sur les chantiers qui l'emploient déjà. <strong>Renommer un code ou ajouter un
            groupe ne change rien aux contre-études déjà chargées</strong> : les postes désignent
            le groupe, pas son nom. Seul le retrait d'un mot-clé peut faire changer d'avis
            l'application sur un poste que personne n'avait encore classé à la main.
          </p>
        </>
      )}

      {etat.phase === "chargee" && (
        <Ajout
          enAttente={enAttente === NOUVEAU}
          onCreer={(champs) => {
            agir(creerGroupe(champs), NOUVEAU);
          }}
        />
      )}
    </>
  );
}
